import { useCallback } from 'react';

import { BffApiUpdateItemError, UpdateItemGqlStatus } from '../../../../types';

import {
  InvoiceID,
  InvoiceNanoID,
  InvoicePaymentMethods,
  InvoiceUUID,
  PayInternalInvoiceCacheKeys
} from '../../invoicesTypes';
import { AccountID } from '../../../accounts/accountsTypes';

import { CACHE_INVOICE_PAY_INTERNAL_QUERY } from '../../queries/cacheInvoicePayInternal.query';
import { CACHE_INVOICE_PAY_INTERNAL_ASYNC_QUERY } from '../../queries/cacheInvoicePayInternalAsync.query';

import { useCacheUpdateQuery } from '../../../common/reactQuery/hooks/useCacheUpdateQuery';

interface CacheInvoicePayInternalOptions {
  cacheKeys?: PayInternalInvoiceCacheKeys;
}

export interface CacheInvoicePayInternalInvoice {
  accountId: AccountID;
  paymentMethod: InvoicePaymentMethods;
}

export type CacheInvoicePayInternalInput = CacheInvoicePayInternalInvoice & {
  uuid: InvoiceID | InvoiceNanoID | InvoiceUUID;
};

export interface CacheInvoicePayInternalHandlerInput {
  invoiceId: InvoiceID;
  async?: boolean;
  invoice: CacheInvoicePayInternalInvoice;
}

export interface CacheInvoicePayInternalResponse {
  invoicePayInternal: {
    status: UpdateItemGqlStatus;
  };
}

export interface CacheInvoicePayInternalAsyncResponse {
  invoicePayInternalAsync: {
    status: UpdateItemGqlStatus;
  };
}

export type CacheInvoicePayInternalError = BffApiUpdateItemError;

const action = 'invoicePayInternal';
const asyncAction = 'invoicePayInternalAsync';

function useCacheInvoicePayInternal<
  CacheInvoicePayInternalRecordType = unknown
>({ cacheKeys }: CacheInvoicePayInternalOptions = {}) {
  const {
    updateCacheQuery: payInternalInvoice,
    updateCacheQueryErrorMessage: payInternalInvoiceErrorMessage,
    updateCacheQueryLoading: payInternalInvoiceLoading,
    updateCacheQueryReset: payInternalInvoiceReset
  } = useCacheUpdateQuery<
    CacheInvoicePayInternalInput,
    CacheInvoicePayInternalResponse,
    CacheInvoicePayInternalError,
    CacheInvoicePayInternalRecordType
  >({ action, query: CACHE_INVOICE_PAY_INTERNAL_QUERY, cacheKeys });

  const {
    updateCacheQuery: payInternalInvoiceAsync,
    updateCacheQueryErrorMessage: payInternalInvoiceAsyncErrorMessage,
    updateCacheQueryLoading: payInternalInvoiceAsyncLoading,
    updateCacheQueryReset: payInternalInvoiceAsyncReset
  } = useCacheUpdateQuery<
    CacheInvoicePayInternalInput,
    CacheInvoicePayInternalAsyncResponse,
    CacheInvoicePayInternalError,
    CacheInvoicePayInternalRecordType
  >({
    action: asyncAction,
    query: CACHE_INVOICE_PAY_INTERNAL_ASYNC_QUERY,
    cacheKeys
  });

  const handlePayInternalInvoice = useCallback<
    (input: CacheInvoicePayInternalHandlerInput) => Promise<unknown>
  >(
    (input) => {
      const mutate = input.async ? payInternalInvoiceAsync : payInternalInvoice;

      return mutate({ uuid: input.invoiceId, ...input.invoice });
    },
    [payInternalInvoice, payInternalInvoiceAsync]
  );

  const handlePayInternalInvoiceReset = useCallback<() => void>(() => {
    payInternalInvoiceReset();
    payInternalInvoiceAsyncReset();
  }, [payInternalInvoiceAsyncReset, payInternalInvoiceReset]);

  return {
    payInternalInvoice: handlePayInternalInvoice,
    payInternalInvoiceErrorMessage:
      payInternalInvoiceErrorMessage || payInternalInvoiceAsyncErrorMessage,
    payInternalInvoiceLoading:
      payInternalInvoiceLoading || payInternalInvoiceAsyncLoading,
    payInternalInvoiceReset: handlePayInternalInvoiceReset
  };
}

export default useCacheInvoicePayInternal;
